import {
  useCompositeItem,
  CompositeItemOptions,
  CompositeItemHTMLProps,
} from 'reakit';
import { createComponent, createHook } from 'reakit-system';
import { ReactNode } from 'react';

import { useStyles } from '../../hooks/useStyles';
import { WHCM, WHCMstyles } from '../../constants';

type ListboxOptionOptions = CompositeItemOptions & {
  children: ReactNode;
  value: string | number;
};
type ListboxOptionHTMLProps = Omit<CompositeItemHTMLProps, 'children'>;

type ListboxOptionProps = Partial<
  ListboxOptionOptions & ListboxOptionHTMLProps
>;

const useListboxOption = createHook<
  ListboxOptionOptions,
  ListboxOptionHTMLProps
>({
  name: 'ListboxOption',
  keys: ['children'],
  compose: [useCompositeItem, useStyles],
  useOptions(options) {
    return {
      ...options,
      styles: {
        display: 'block',
        px: 'sp-16',
        py: 'sp-8',
        width: '100%',
        textAlign: 'left',
        cursor: 'pointer',
        mt: 'sp-0',
        '&[data-current="true"]': {
          backgroundColor: 'comboboxOptionHoverBg',
        },
        '&[data-selected="true"]': {
          backgroundColor: 'comboboxOptionSelectedBg',
          outline: 'none',
        },
        ':active': {
          backgroundColor: 'comboboxOptionActiveBg',
        },
        ':focus-visible': {
          outline: 'none',
        },
        [WHCM]: {
          '&[data-selected="true"], :focus-visible, &[data-current="true"]': {
            ...WHCMstyles,
            backgroundColor: 'Highlight',
            color: 'HighlightText',
          },
        },
      },
    };
  },
  useProps({ children }, props) {
    return {
      ...props,
      children,
      role: 'option',
    };
  },
});

const ListboxOptionBase = createComponent({
  as: 'div',
  useHook: useListboxOption,
  memo: true,
});

// В React.createElement прокидывается название компонента - ListboxOption
// Необходимо при разделении списка опций для выбора и вспомогательных сообщений
export function ListboxOption(props: ListboxOptionProps) {
  return <ListboxOptionBase {...props} />;
}
